/**
 * Provider-agnostic icon resolution — the framework-free registry.
 *
 * An {@link IconProvider} turns a prefixed icon name (e.g. `fa-solid-truck`,
 * `lucide-settings`) into a raw, inline-ready SVG string. Providers are
 * registered once — typically by the shell at boot — and consumed everywhere via
 * {@link resolveIcon} (or the framework `useIcon` composables built on top of it).
 *
 * Accepted in ADR 2026-03-19 (Icon Architecture: Provider-Agnostic System). This
 * module has NO framework dependency, so it is importable from the shell, abax-ui,
 * dev-shell, and any MFE.
 *
 * SECURITY INVARIANT: every value this module writes to the cache passes through
 * {@link sanitizeSvg} — via {@link resolveIcon} (provider results) or
 * {@link addIcon} (bundled primes) — AND every value it hands out passes through
 * {@link readSanitized}. The cache is shared with every other copy of this module
 * on the page, and an older copy may have sanitized by weaker rules, so a write
 * elsewhere is never trusted on read. Every consumer feeding the result into a
 * `v-html` / `dangerouslySetInnerHTML` sink relies on this. Never add a cache
 * write or read that bypasses these two.
 */
import DOMPurify from 'dompurify';

export interface IconProvider {
  /** Routing key — the segment before the first `-` in an icon name (e.g. `fa`, `lucide`). */
  prefix: string;
  /**
   * Resolve a full icon name to a sanitized SVG string. Implementations MUST
   * normalize `fill` to `currentColor` so the rendered icon inherits CSS color.
   */
  resolve(name: string): Promise<string>;
  /**
   * Optional synchronous cache primer. The canonical way to pre-seed the shared
   * cache is the exported {@link addIcon}; this hook exists for providers that
   * also want to expose their own primer. Not invoked by the registry itself.
   */
  addIconSync?(name: string, svg: string): void;
}

interface IconRegistry {
  /** prefix -> provider */
  providers: Map<string, IconProvider>;
  /** full icon name -> resolved SVG string */
  cache: Map<string, string>;
  /** full icon name -> in-flight resolve (dedupes concurrent requests) */
  pending: Map<string, Promise<string>>;
}

// The registry is a process-wide singleton anchored to `window`, mirroring
// `getSharedEventBus`. @abax/platform is NOT shared as a Module Federation
// singleton, so the shell and abax-ui each evaluate their own copy of this
// module — a module-local registry would leave providers the shell registered
// invisible to abax-ui's <AxIcon>. The window anchor gives every copy one
// registry. Falls back to a module-local registry where `window` is absent
// (SSR, Node test env).
const REGISTRY_KEY = '__ABAX_ICON_REGISTRY__';
let fallbackRegistry: IconRegistry | undefined;

function createRegistry(): IconRegistry {
  return { providers: new Map(), cache: new Map(), pending: new Map() };
}

function getRegistry(): IconRegistry {
  if (typeof window === 'undefined') {
    fallbackRegistry ??= createRegistry();
    return fallbackRegistry;
  }
  const w = window as unknown as Record<string, IconRegistry | undefined>;
  w[REGISTRY_KEY] ??= createRegistry();
  return w[REGISTRY_KEY] as IconRegistry;
}

function prefixOf(name: string): string {
  const dash = name.indexOf('-');
  return dash === -1 ? name : name.slice(0, dash);
}

/**
 * Defence-in-depth before any SVG reaches a `v-html` / `dangerouslySetInnerHTML`
 * sink. Providers are trusted to return well-formed SVG, but a provider bug — or a
 * bundled string passed to {@link addIcon} — must never carry executable content
 * into the cache. Parses with a real DOM and keeps only the SVG profile (safe SVG
 * elements + filters); scripts, event handlers, foreign content, and `javascript:`
 * URLs are removed. DOM-parser-based on purpose: a regex sanitizer loses to
 * mutation XSS, where the browser re-parses the string differently than the filter
 * did (unquoted/slash-joined handlers, `<style>` breakouts). Applied at every cache write.
 *
 * DOMPurify needs a DOM, and without one it has no `sanitize` at all. Server-side
 * rendering still reaches a sink (the rendered HTML carries the cache to the
 * browser), and any pattern-based stand-in is bypassed by entity-encoded URLs or
 * `srcdoc`, so an unparseable context caches nothing and the browser resolves the
 * icon itself. A server with a DOM shim (jsdom) takes the full path.
 */
function sanitizeSvg(svg: string): string {
  if (!DOMPurify.isSupported) return '';
  return DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true, svgFilters: true } });
}

// Raw cache value -> what this copy returns for it. The shared cache can hold a
// value another copy wrote, so each distinct value is sanitized here once.
const sanitizedByThisCopy = new Map<string, string>();

function readSanitized(svg: string): string {
  let safe = sanitizedByThisCopy.get(svg);
  if (safe === undefined) {
    safe = sanitizeSvg(svg);
    sanitizedByThisCopy.set(svg, safe);
    sanitizedByThisCopy.set(safe, safe);
  }
  return safe;
}

/**
 * Register an icon provider keyed by its {@link IconProvider.prefix}.
 * Re-registering the same prefix replaces the previous provider.
 */
export function registerIconProvider(provider: IconProvider): void {
  getRegistry().providers.set(provider.prefix, provider);
}

/**
 * Synchronously seed the shared cache with an already-known SVG string. Use for
 * boot-critical icons that are bundled rather than fetched, so the first render
 * has zero latency (ADR R-ICON-11). Sanitized on write (see the security invariant).
 */
export function addIcon(name: string, svg: string): void {
  getRegistry().cache.set(name, readSanitized(svg));
}

/** Synchronously read a cached icon, or `undefined` if it has not resolved yet. */
export function peekIcon(name: string): string | undefined {
  const cached = getRegistry().cache.get(name);
  return cached === undefined ? undefined : readSanitized(cached);
}

/**
 * Resolve an icon name to an SVG string. Returns the cached value immediately
 * when present, dedupes concurrent in-flight resolves, and routes to the provider
 * matching the name's prefix. Rejects when no provider is registered for the prefix.
 * Successful resolutions populate the shared cache (sanitized) as a side effect.
 */
export function resolveIcon(name: string): Promise<string> {
  const registry = getRegistry();

  const cached = registry.cache.get(name);
  if (cached !== undefined) return Promise.resolve(readSanitized(cached));

  const inFlight = registry.pending.get(name);
  if (inFlight) return inFlight.then(readSanitized);

  const provider = registry.providers.get(prefixOf(name));
  if (!provider) {
    return Promise.reject(
      new Error(`No icon provider registered for prefix "${prefixOf(name)}" (icon "${name}")`),
    );
  }

  const request = provider
    .resolve(name)
    .then((rawSvg) => {
      const svg = readSanitized(rawSvg);
      registry.cache.set(name, svg);
      registry.pending.delete(name);
      return svg;
    })
    .catch((err) => {
      registry.pending.delete(name);
      throw err;
    });

  registry.pending.set(name, request);
  return request;
}

/** @internal Test/HMR helper — clears the registered providers and caches. */
export function __resetIconRegistry(): void {
  const registry = getRegistry();
  registry.providers.clear();
  registry.cache.clear();
  registry.pending.clear();
  sanitizedByThisCopy.clear();
}
